/**
 * `redux-store/saga/business` — the COMMON half. It holds the one worker written new for both
 * platforms, `fetchBranchLicencesFunc` (2026-10-30); every worker uplifted from the apps differs
 * and stays in its variant, as the table below says.
 *
 * **Which kind of divergence is this? PERMANENT — every worker differs, and not by an accident
 * of style.** The two platforms have genuinely different business/user-data flows:
 *
 * | worker | why it could not be shared |
 * |---|---|
 * | `getBusinessData` | web forwards a loader flag, invokes a caller callback, gates on `!response?.code`, sets a window flag, chains a branch-data fetch and dispatches a completion action in `finally`. Mobile's is five lines with none of that. |
 * | `getUserData` | web redirects BEFORE dispatching success and has a `finally`; mobile dispatches success first, then triggers a catalogue reload the other platform has no equivalent of. |
 * | `updateExistingUserLicence` | web re-fetches the user unconditionally; mobile only when `!response?.code`. |
 * | `fetachPermittedLicences` | identical except web adds a `skipGlobalLoader` key to the request config. Following the `saga/sale` precedent: a differing request CONFIG is not the same request, so the bodies stay apart. |
 * | `root` | different effects (web is all `takeEvery`; mobile uses `takeLatest` for three) and different worker sets. |
 *
 * Web-only workers: `fetchMasterDataPicklist`. Mobile-only: `getUserDataOnly`,
 * `updateExistingUserSalesLicence`. Their slice actions are one-sided in core too, which is the
 * independent confirmation that this split is real rather than a transcription artefact.
 *
 * ⚠️ The misspelling `fetachPermittedLicences` is the apps' own, on BOTH platforms. Preserved —
 * renaming it is a separate change with its own blast radius.
 *
 * Each variant's `root()` is its own; `export *` does not forward a default.
 */
import { put } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";

import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { METHODS } from "../../../data/constants";
import { setBranchLicences } from "../../slices/BusinessSlice";
import type { GetBranchLicencesPayload } from "../../slices/BusinessSlice/shared";

/** The part of a `branch/filter` row this saga reads. */
interface BranchRecord {
  branchId?: string;
  availableLicences?: unknown;
}

/**
 * `branchId`'s licence codes from a `branch/filter` answer, or `null` when the backend did not
 * say. An error answer is an object with a `code` (or nothing at all), not a list.
 */
export function readBranchLicences(response: unknown, branchId: string): string[] | null {
  const branch = Array.isArray(response)
    ? (response as BranchRecord[]).find((candidate) => candidate?.branchId === branchId)
    : undefined;
  const licences = branch?.availableLicences;
  if (!Array.isArray(licences)) return null;
  return licences.filter((code): code is string => typeof code === "string");
}

/**
 * `getBranchLicences`: the branch's own licences, `POST branch/filter?includeLicences=true` for
 * this branch alone (rista-be `branch_controller.filterBranches`; only with `includeLicences` does a
 * row carry `availableLicences`). Stores them in `businessStore.branchLicences` and calls back with
 * them, or with `null`. The callback runs on a failure too, so a caller waiting on it never hangs.
 *
 * No loader on either platform: web's interceptor reads `skipGlobalLoader`, mobile's `hideLoader`.
 * Registered with `takeEvery` on both: `takeLatest` would cancel a first request and its callback
 * would never run. A second request for the same load is already joined in
 * `commonComponents/LeftMenu/branchLicences`.
 */
export function* fetchBranchLicencesFunc(action: PayloadAction<GetBranchLicencesPayload>): any {
  const { branchId, callback } = action.payload;
  let licences: string[] | null = null;
  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.POS.getAllBranches({ includeLicences: true }),
      data: { branchIds: [branchId] },
      hideLoader: true,
      skipGlobalLoader: true,
    } as any);
    licences = readBranchLicences(response, branchId);
    if (licences) yield put(setBranchLicences({ branchId, licences }));
  } catch (error) {
    console.log(error);
  } finally {
    callback?.(licences);
  }
}
